import _ from 'lodash';
import { sqSummaryReportStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import {
  extractErrorMessage,
  fetchSpcChartTable,
  fetchStatsSummaryData,
} from '@/summaryReport/summaryReport.utilities';
import { ALIGN_WEEKLY_ID } from '@/tools/statisticsSummary/statisticsSummary.constants';
import { setTrendItemColor } from '@/trendData/trend.actions';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';
import { errorToast } from '@/utilities/toast.utilities';
import { sqFormulasApi } from '@seeqdev/api/endpoint/FormulasApi';
import type { GenericTableOutput } from 'generated-plugin-api/seeq';
import type { X_AXIS_LABEL, Y_AXIS_LABEL } from '@/summaryReport/summaryData.constants';
import type { ChartPoint, StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import type { ItemSearchPreviewV1 } from '@seeqdev/api/model/ItemSearchPreviewV1';

type SummaryReportItemPreview = ItemSearchPreviewV1 & { color?: string };

export interface ItemSignalAndCondition {
  signal?: SummaryReportItemPreview;
  condition?: SummaryReportItemPreview;
  alignmentCondition?: SummaryReportItemPreview & {
    conditionMetadata?: { maximumDuration?: { value?: number | null; uom?: string | null } };
  };
  formula?: string;
}

export function setItems(items: StatisticsSummaryItem[]): void {
  flux.dispatch('SR_SET_ITEMS', { items });
}

async function fetchAndBuildItemPayload(item: StatisticsSummaryItem): Promise<StatisticsSummaryItem> {
  const result = await fetchStatsSummaryData(item.id);
  const { signal, condition, alignmentCondition, formula } = await fetchItemSignalAndCondition(item.id);

  const hasXbarSpcChart = !!(result as StatisticsSummaryItem)?.data?.spcCharts?.['XBAR'];
  const alignmentConditionId =
    hasXbarSpcChart && !alignmentCondition?.id ? ALIGN_WEEKLY_ID : (alignmentCondition?.id ?? '');

  let spcChartTable;
  let warnings;
  if (signal?.id) {
    const chartTableResponse = await fetchSpcChartTable(
      item.id,
      signal.id,
      condition?.id,
      alignmentConditionId,
      alignmentCondition?.conditionMetadata,
      formula,
    );
    spcChartTable = chartTableResponse.table;
    if (chartTableResponse.warningLogs && chartTableResponse.warningLogs.length > 0) {
      const warningMessages = new Set<string>();
      chartTableResponse.warningLogs.forEach((log: any) => {
        if (log.formulaLogEntries) {
          Object.values(log.formulaLogEntries).forEach((entry: any) => {
            if (entry.logDetails) {
              entry.logDetails.forEach((detail: any) => {
                if (detail.message) {
                  // Add period if not present, deduplicate messages
                  const message = detail.message.trim();
                  const messageWithPeriod = message.endsWith('.') ? message : `${message}.`;
                  warningMessages.add(messageWithPeriod);
                }
              });
            }
          });
        }
      });
      if (warningMessages.size > 0) {
        warnings = Array.from(warningMessages).map((message) => ({ message }));
      }
    }
  }

  return { ...item, ...result, tableData: spcChartTable as GenericTableOutput, warnings, error: undefined };
}

export async function addStatsSummary(item: StatisticsSummaryItem): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      flux.dispatch('SR_ADD_ITEM', { item: payload });
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      flux.dispatch('SR_ADD_ITEM', { item: { ...item, error: { message: errorMessage } } });
    })
    .finally(() => {
      setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function insertStatsSummaryAtIndex(item: StatisticsSummaryItem, index: number): Promise<void> {
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      flux.dispatch('SR_INSERT_ITEM_AT', { item: payload, index });
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      flux.dispatch('SR_INSERT_ITEM_AT', { item: { ...item, error: { message: errorMessage } }, index });
    })
    .finally(() => {
      setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function refreshStatsData(id: string): Promise<void> {
  const item = sqSummaryReportStore.findItem(id);
  if (!item) {
    return;
  }
  await fetchAndBuildItemPayload(item)
    .then((payload) => {
      flux.dispatch('SR_UPDATE_ITEM', { item: payload });
      setTrendItemColor(payload.id, payload.color);
    })
    .catch((error) => {
      const errorMessage = extractErrorMessage(error);
      flux.dispatch('SR_UPDATE_ITEM', { item: { ...item, error: { message: errorMessage } } });
    })
    .finally(() => {
      setStatus(ITEM_DATA_STATUS.PRESENT);
    });
}

export async function fetchItemsSignalAndConditionMap(
  items: StatisticsSummaryItem[],
): Promise<Record<string, ItemSignalAndCondition>> {
  const signalAndConditionMap: Record<string, ItemSignalAndCondition> = {};

  await Promise.all(
    items.map(async (item: StatisticsSummaryItem) => {
      signalAndConditionMap[item.id] = await fetchItemSignalAndCondition(item.id);
    }),
  );

  return signalAndConditionMap;
}

export async function fetchItemSignalAndCondition(itemId: string): Promise<ItemSignalAndCondition> {
  try {
    const { data } = await sqFormulasApi.getItem({ id: itemId });
    const signal = _.find(data.parameters, (param) => param.name === 'signal')?.item as SummaryReportItemPreview;
    const condition = _.find(data.parameters, (param) => param.name === 'condition')?.item as SummaryReportItemPreview;
    const alignmentCondition = _.find(data.parameters, (param) => param.name === 'alignmentCondition')
      ?.item as SummaryReportItemPreview;
    return { signal, condition, alignmentCondition, formula: data.formula };
  } catch (error) {
    // TODO CRAB-53442 - Add improved and consistent error and redaction handling
    errorToast({ httpResponseOrError: error });
    return { signal: undefined, condition: undefined };
  }
}

export function setBaseline(item: StatisticsSummaryItem | null): void {
  flux.dispatch('SR_SET_BASELINE', { item });
}

export function removeItems(items: StatisticsSummaryItem[]): void {
  flux.dispatch('SR_REMOVE_ITEMS', { items });
}

export function setIsItemsOverlaid(isItemsOverlaid: boolean): void {
  flux.dispatch('SR_SET_IS_ITEMS_OVERLAID', { isItemsOverlaid });
}

export function setStatus(status: ITEM_DATA_STATUS): void {
  flux.dispatch('SR_SET_STATUS', { status });
}

export function setShowHistogram(showHistogram: boolean): void {
  flux.dispatch('SR_SET_SHOW_HISTOGRAM', { showHistogram });
}

export function setShowBoxPlot(showBoxPlot: boolean): void {
  flux.dispatch('SR_SET_SHOW_BOX_PLOT', { showBoxPlot });
}

export function setShowControlCharts(showControlCharts: boolean): void {
  flux.dispatch('SR_SET_SHOW_CONTROL_CHARTS', { showControlCharts });
}

export function setShowTable(showTable: boolean): void {
  flux.dispatch('SR_SET_SHOW_TABLE', { showTable });
}

export function setVisibleStats(visibleStats: string[]): void {
  visibleStats.sort();
  flux.dispatch('SR_SET_VISIBLE_STATS', { visibleStats });
}

export function setShowBellCurve(showBellCurve: boolean): void {
  flux.dispatch('SR_SET_SHOW_BELL_CURVE', { showBellCurve });
}

export function setXAxisLabel(xAxisLabel: X_AXIS_LABEL): void {
  flux.dispatch('SR_SET_X_AXIS_LABEL', { xAxisLabel });
}

export function setYAxisLabel(yAxisLabel: Y_AXIS_LABEL): void {
  flux.dispatch('SR_SET_Y_AXIS_LABEL', { yAxisLabel });
}

export function setShowSignal(showSignal: boolean): void {
  flux.dispatch('SR_SET_SHOW_SIGNAL', { showSignal });
}

export function setShowTimeRange(showTimeRange: boolean): void {
  flux.dispatch('SR_SET_SHOW_TIME_RANGE', { showTimeRange });
}

export function setShowCondition(showCondition: boolean): void {
  flux.dispatch('SR_SET_SHOW_CONDITION', { showCondition });
}

export function setShowBoxplotPoints(showBoxplotPoints: boolean): void {
  flux.dispatch('SR_SET_SHOW_BOXPLOT_POINTS', { showBoxplotPoints });
}

export function setShowSpecificationLimits(showSpecificationLimits: boolean): void {
  flux.dispatch('SR_SET_SHOW_SPECIFICATION_LIMITS', { showSpecificationLimits });
}

export function setShowSigmaLevels(showSigmaLevels: boolean): void {
  flux.dispatch('SR_SET_SHOW_SIGMA_LEVELS', { showSigmaLevels });
}

export function setShowControlLimits(showControlLimits: boolean): void {
  flux.dispatch('SR_SET_SHOW_CONTROL_LIMITS', { showControlLimits });
}

export function setShowRunRules(showRunRules: boolean): void {
  flux.dispatch('SR_SET_SHOW_RUN_RULES', { showRunRules });
}

export function setShowRunRuleViolations(showRunRuleViolations: boolean): void {
  flux.dispatch('SR_SET_SHOW_RUN_RULE_VIOLATIONS', { showRunRuleViolations });
}

export function setShowIssues(showIssues: boolean): void {
  flux.dispatch('SR_SET_SHOW_ISSUES', { showIssues });
}

export function resetView(): void {
  flux.dispatch('SR_RESET_VIEW');
}

export function setSelectedPoints(selectedPoints: ChartPoint[]): void {
  flux.dispatch('SR_SET_SELECTED_POINTS', { selectedPoints });
}

export async function fetchStatSummaries(): Promise<void> {
  const items = sqSummaryReportStore.items;
  if (!items?.length) return;

  setStatus(ITEM_DATA_STATUS.LOADING);
  try {
    const updatedItems = await Promise.all(
      _.map(items, (item) =>
        fetchAndBuildItemPayload(item).catch((error) => {
          const errorMessage = extractErrorMessage(error);
          return { ...item, error: { message: errorMessage } };
        }),
      ),
    );
    setItems(updatedItems);
  } finally {
    setStatus(ITEM_DATA_STATUS.PRESENT);
  }
}
